import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { STATE_STATUS } from '../constants';

import { mapArrayToObjectReducer } from '@awp/helpers/Array';

const initialState = {
  observationData: {},
  observations: {},
  status: STATE_STATUS.IDLE,
  error: null,
};

const OBSERVATION_INDEX_SIZE = 60 * 60 * 24 * 30; //30 days

export const selectAllObservations = (state) => state.observations;
export const isLoading = (state) => state.observations.status == STATE_STATUS.LOADING;
export const currentWeatherStationId = (state) => Number.parseInt(state.graphViewPage.weatherStation, 10);
export const selectObservationsByWeatherStationId = (weatherStationId) => (state) => {
  if (!weatherStationId) return {};
  return state.observations.observations[weatherStationId] || {};
};

const getIndexStartTimestamp = (index) => index * OBSERVATION_INDEX_SIZE * 1000;
const getIndexEndTimestamp = (index) => (index + 1) * OBSERVATION_INDEX_SIZE * 1000;

export const loadDatasetIndex = createAsyncThunk(
  'observations/loadDatasetIndex',
  async (args, { getState }) => {
    const { DatasetId, IndexId } = args;
    const currentDataset = getState().dataSets.dataSets.filter((ds) => ds.Id === DatasetId);

    if (currentDataset.length < 1) {
      throw new Error('Loading unknown dataset');
    }

    const baseUrl = currentDataset[0].Url;
    const formattedStartDate = format(new Date(getIndexStartTimestamp(IndexId)), 'yyyy-MM-dd-HH-mm');
    const formattedEndDate = format(new Date(getIndexEndTimestamp(IndexId)), 'yyyy-MM-dd-HH-mm');
    const url = `${baseUrl}?from=${formattedStartDate}&to=${formattedEndDate}&includeinterval=true&includeobsservationtype=true`;
    const response = await fetch(url);
    return await response.json();
  },
  {
    condition: (args, { getState }) => {
      const { observations: state } = getState();
      const { DatasetId, IndexId, IsRefreshed } = args;
      //Load if index was not initiated and added yet.
      return (
        !state.observationData[DatasetId] || !state.observationData[DatasetId].Data[IndexId] || (IsRefreshed ?? false)
      );
    },
  },
);

export const getObservationIntervals = (DatasetId) => (state) => {
  const { observations } = state;
  return DatasetId.map((id) => {
    if (!observations.observationData[id]) return [id, null];

    return [id, observations.observationData[id].Metadata.Interval || null];
  }).reduce(mapArrayToObjectReducer, {});
};

export const getObservationDataArray = (DatasetId, startTimeStamp, endTimeStamp) => (state) => {
  return DatasetId.map((id) => [id, getObservationData(id, startTimeStamp, endTimeStamp)(state)]).reduce(
    mapArrayToObjectReducer,
    {},
  );
};

export const getObservationData = (DatasetId, startTimeStamp, endTimeStamp) => (state) => {
  const startIdx = getIndexId(startTimeStamp),
    endIdx = getIndexId(endTimeStamp);
  const { observations } = state;
  let startTimestampLocation = 0,
    endTimestampLocation = 0,
    currentIdx = startIdx,
    result = [];
  if (!observations.observationData[DatasetId]) return result;

  do {
    if (observations.observationData[DatasetId].Data[currentIdx]) {
      switch (true) {
        case currentIdx == endIdx && currentIdx == startIdx:
          startTimestampLocation = findLocationIndex(
            observations.observationData[DatasetId].Data[currentIdx].Observations,
            startTimeStamp,
          );
          endTimestampLocation = findLocationIndex(
            observations.observationData[DatasetId].Data[currentIdx].Observations,
            endTimeStamp,
          );
          break;
        case currentIdx == startIdx:
          startTimestampLocation = findLocationIndex(
            observations.observationData[DatasetId].Data[currentIdx].Observations,
            startTimeStamp,
          );
          endTimestampLocation = observations.observationData[DatasetId].Data[currentIdx].Observations.length - 1;
          break;
        case currentIdx == endIdx:
          startTimestampLocation = 0;
          endTimestampLocation = findLocationIndex(
            observations.observationData[DatasetId].Data[currentIdx].Observations,
            endTimeStamp,
          );
          break;
        default:
          startTimestampLocation = 0;
          endTimestampLocation = observations.observationData[DatasetId].Data[currentIdx].Observations.length - 1;
          break;
      }

      result = result.concat(
        observations.observationData[DatasetId].Data[currentIdx].Observations.slice(
          startTimestampLocation,
          endTimestampLocation,
        ),
      );
    }
    currentIdx++;
  } while (currentIdx <= endIdx);
  return result;
};

const findLocationIndex = (array, element) => {
  let lIdx = 0,
    hIdx = array.length;

  while (lIdx < hIdx) {
    let mIdx = (lIdx + hIdx) >>> 1;
    if (array[mIdx].TimeStamp < element) {
      lIdx = mIdx + 1;
    } else {
      hIdx = mIdx;
    }
  }

  return lIdx;
};

export const getIndexId = (ts) => Math.floor(ts / OBSERVATION_INDEX_SIZE);

export const observationsSlice = createSlice({
  name: 'observations',
  initialState,
  reducers: {},
  extraReducers: {
    [loadDatasetIndex.pending]: (state, action) => {
      const { DatasetId, IndexId } = action.meta.arg;
      if (!state.observationData[DatasetId]) state.observationData[DatasetId] = { Metadata: {}, Data: {} };

      state.observationData[DatasetId].Data[IndexId] = {
        Observations: [],
        Status: new String(STATE_STATUS.LOADING).toString(),
      };
    },
    [loadDatasetIndex.fulfilled]: (state, action) => {
      const { DatasetId, IndexId } = action.meta.arg;
      state.observationData[DatasetId].Data[IndexId].Status = new String(STATE_STATUS.SUCCESS).toString();
      action.payload.forEach((observationDataset) => {
        const { Data, ...Metadata } = observationDataset;
        state.observationData[DatasetId].Metadata = Metadata;
        state.observationData[DatasetId].Data[IndexId].Observations = Data;
      });
    },
    [loadDatasetIndex.rejected]: (state, action) => {
      const { DatasetId, IndexId } = action.meta.arg;
      state.observationData[DatasetId].Data[IndexId].Status = new String(STATE_STATUS.FAILED).toString();
      state.observationData[DatasetId].Data[IndexId].Error = action.error.message;
    },
  },
});
